import { css, cx } from '@fable/theme'
import { FlexBox, Text } from '@fable/components'
import { Flower } from './flower/Flower'
import { formatK } from '@fable/utils'
import { gsap } from 'gsap'
import { MotionPathPlugin } from 'gsap/MotionPathPlugin'
import { ReadingGoalCompleteBg } from './complete/ReadingGoalCompleteBg'
import { useGSAP } from '@gsap/react'
import { useReadingGoals } from '../../lib/ctx/reading_goals'
import { useStats } from '../../lib/ctx/stats'
import { useRef, useState } from 'react'
import { FlowerHeadType, ReadingGoal } from '../../lib/types'
import { useStatsTheme } from '../../theme/ctx'
import { useStatsPermission } from '../../lib/hooks'
import { sendHapticOneShot } from 'app/webview/utils'

gsap.registerPlugin(useGSAP, Physics2DPlugin, MorphSVGPlugin, MotionPathPlugin)

interface Props {
  goal: ReadingGoal
  stretched?: boolean
  delay?: number
  onPetalsFilled?: () => void
  startPetalAnim?: boolean
}

export const ReadingGoalCard = ({
  goal,
  stretched = false,
  delay = 0,
  onPetalsFilled,
  startPetalAnim,
}: Props) => {
  const { isAndroid } = useStatsPermission()
  const isSelfView = useStats((s) => s.isSelfView)
  const theme = useStatsTheme((s) => s.theme)
  const [edit, period] = useReadingGoals((s) => [s.editReadingGoal, s.period])
  const complete =
    goal.number_goal !== 0 && goal.number_achieved >= goal.number_goal
  const container = useRef(null)
  const [viewed, setViewed] = useState(false)

  useGSAP(
    () => {
      const tl = gsap.timeline({
        onComplete: () => setViewed(true),
        scrollTrigger: {
          trigger: container.current,
          start: 'bottom bottom',
          once: true,
        },
      })

      tl.to('.anim__reading-goal-card', {
        opacity: 1,
        duration: 0.3,
        ease: 'power2.inOut',
      })
      tl.addPause(0.5)
    },
    { scope: container }
  )

  const getLabel = () => {
    switch (goal.goal_type) {
      case 'books_finished':
        return 'Books finished'
      case 'pages_read':
        return 'Pages read'
      case 'active_days':
        return 'Active days'
    }
  }

  const getFlowerType = (): FlowerHeadType => {
    switch (goal.goal_type) {
      case 'books_finished':
        return 'round'
      case 'pages_read':
        return 'spike'
      case 'active_days':
        return 'wavy'
      default:
        return 'round'
    }
  }

  const handleEditGoal = (id: string) => {
    if (!isSelfView) return
    if (isAndroid && period !== 'year') return

    edit(goal.id)
    sendHapticOneShot('light')
  }

  return (
    <div ref={container}>
      <FlexBox
        role="button"
        aria-label="edit goal"
        data-testid="reading-goal-card"
        className={cx(
          'anim__reading-goal-card',
          css`
            padding: 12px;
            width: 100%;
            height: 100%;
            background-color: ${theme.colors.section.bgSecondary};
            border-radius: 16px;
            gap: 15px;
            position: relative;
            overflow: hidden;
            opacity: 0;
            will-change: opacity;
          `
        )}
        onClick={() => handleEditGoal(goal.id)}
        flexDirection="column"
        alignItems="center"
        justifyContent="flex-end"
      >
        {complete ? (
          <ReadingGoalCompleteBg stretched={stretched} motion={{ delay: 1 }} />
        ) : null}
        <FlexBox
          className={css`
            width: 100%;
            max-width: 110px;
            height: 140px;
            position: relative;
            z-index: 1;
          `}
        >
          <Flower
            id={goal.id}
            head={getFlowerType()}
            face={goal.progress_ratio === 'behind' ? 'sleepy' : 'happy'}
            progress={goal.number_achieved}
            total={goal.number_goal}
            motion={{ delay }}
            startPetalAnim={viewed && startPetalAnim}
            onPetalsFilled={onPetalsFilled}
          />
        </FlexBox>
        <FlexBox
          className={css`
            width: 100%;
            position: relative;
            z-index: 1;
          `}
          alignItems="center"
          flexDirection="column"
        >
          <Text
            className={css`
              font-size: 24px;
              line-height: 28px;
              letter-spacing: -0.5px;
            `}
            tag="h3"
            type="header"
            weight="700"
            align="center"
            color={theme.colors.section.textReadingGoal}
          >
            <span
              className={css`
                color: ${theme.colors.primary};
              `}
            >
              {formatK(goal.number_achieved).toString()}
            </span>{' '}
            of {formatK(goal.number_goal).toString()}
          </Text>
          <Text
            className={css`
              font-size: 12px;
              line-height: 16px;
              letter-spacing: -0.2px;
            `}
            type="body"
            width="500"
            align="center"
            color={theme.colors.section.textSecondary}
          >
            {getLabel()}
          </Text>
        </FlexBox>
      </FlexBox>
    </div>
  )
}
